import React, { useEffect, useRef, useState } from 'react';
import { dvaCoveka, spratIzAdrese } from '../adrese/sprat';
import { api } from '../services/api';
import Dialog from './Dialog';
import { useSignedIn } from '../session';
import { AddressSuggestion, GeocodeMatchLevel, PlaceSuggestion } from '../types';
import AddressMap from './AddressMap';

/** Ono što je komponenti potrebno o adresi, bez obzira odakle je došla. */
export interface FixableAddress {
  id: number;
  erpDocumentKey: string;
  rawAddress: string;
  rawCity: string;
  resolvedCity: string | null;
  postalCode: string | null;
  latitude: number | null;
  longitude: number | null;
  matchLevel: GeocodeMatchLevel;
  note: string | null;
  placeSuggestions: PlaceSuggestion[];
}

interface Props {
  address: FixableAddress;
  /** Ko potvrđuje. Do prijavljivanja je to slobodan tekst. */
  /** Zove se posle uspešne potvrde ili odbijanja. */
  onDone: (addressId: number) => void;
}

interface Pin {
  latitude: number;
  longitude: number;
}

/**
 * Ispravka jedne adrese: mapa, predlog savetnika, pristup, potvrda.
 *
 * Isti posao rade operater u magacinu i poslovođa u prodavnici, samo iz
 * različitih razloga — operater ih radi desetine nedeljno u nizu, poslovođa
 * jednu po jednu dok mu je kupac na vezi. Zato je ovo jedna komponenta: da se
 * pravilo o tome šta sme u rutu ne raziđe na dva ekrana.
 *
 * Sprat i lift se traže baš ovde jer se odgovor dobija od kupca, a ne sa mape.
 */
export default function AddressFixer({
  address, onDone
}: Props) {
  const signedIn = useSignedIn();
  const [pin, setPin] = useState<Pin | null>(null);
  const [floor, setFloor] = useState('');
  const [hasLift, setHasLift] = useState(false);
  const [twoPeople, setTwoPeople] = useState(false);
  // Dok operater sam ne dirne „dva čoveka", kućica prati predlog iz sprata i
  // lifta (analiza 2.0, G5). Kad je dirne, njegova reč važi.
  const [twoPeopleTouched, setTwoPeopleTouched] = useState(false);
  // Pin mape koji nije na kući traži svesnu potvrdu dok ga niko ne pomeri (G7).
  const [svesno, setSvesno] = useState(false);
  const [accessNotes, setAccessNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [suggestions, setSuggestions] = useState<AddressSuggestion[] | null>(null);
  const [unverified, setUnverified] = useState<string[]>([]);
  const [asking, setAsking] = useState(false);

  /** Odakle je pin, kad nije iz onoga što piše na otpremnici. */
  const [pinFrom, setPinFrom] = useState<string | null>(null);

  /**
   * Predlog savetnika po kom je postavljen pin. Dok pin stoji tačno tamo, to je
   * pin SAVETNIKA, ne čoveka — server ga ne razlikuje sam (K10, 16.10.2026).
   */
  const [predlogPin, setPredlogPin] = useState<AddressSuggestion | null>(null);

  /** Za koju adresu je savetnik već pitan, da se isti poziv ne plaća dvaput. */
  const askedFor = useRef<number | null>(null);

  // Pri prelasku na drugu adresu pin skače na predlog geokodera, a podaci o
  // pristupu se brišu — inače bi sprat prethodne adrese tiho ostao na sledećoj.
  useEffect(() => {
    setPin(
      address.latitude !== null && address.longitude !== null
        ? { latitude: address.latitude, longitude: address.longitude }
        : null
    );
    // Sprat iz adrese („49/14/04" → 4) se ne kuca ponovo — vidi adrese/sprat.ts.
    const izAdrese = spratIzAdrese(address.rawAddress);
    setFloor(izAdrese === null ? '' : String(izAdrese));
    setHasLift(false);
    setTwoPeople(dvaCoveka(izAdrese, false));
    setTwoPeopleTouched(false);
    setSvesno(false);
    setAccessNotes('');
    setSuggestions(null);
    setUnverified([]);
    setPinFrom(null);
    setPredlogPin(null);
    setError(null);
    // Zavisi se od ŠIFRE, ne od objekta: ekran se sam osvežava i tada dobija
    // nove objekte za iste adrese. Sa objektom u zavisnostima se sprat i
    // pristup brisali usred kucanja (prijavljeno 27.08.2026).
  }, [address.id]);

  /**
   * Pita savetnika kako bi se ulica mogla zvati.
   *
   * Prazan spisak je uredan ishod — savetnik ćuti kad nema razložan predlog,
   * jer je nagađanje gore od ćutanja. Kvar se prijavljuje odvojeno, da se ta
   * dva ne pomešaju.
   */
  const askAdviser = async () => {
    setAsking(true);
    setError(null);
    try {
      const result = await api.suggestAddress(address.id);
      setSuggestions(result.suggestions);
      setUnverified(result.unverified);

      // Adresa bez tačke dobija pin odmah, iz najboljeg predloga. Pomera se i
      // dalje rukom i ništa nije potvrđeno dok se ne pritisne dugme — ali se ne
      // gleda prazna mapa kad rešenje već postoji.
      if (result.suggestions.length > 0 && address.latitude === null) {
        const best = result.suggestions[0];
        setPin({ latitude: best.latitude, longitude: best.longitude });
        setPinFrom(best.address);
        setPredlogPin(best);
        setSvesno(false);
      }

      if (!result.available) {
        setError('Savetnik za adrese nije podešen — nedostaje ključ.');
      } else if (result.error) {
        setError(result.error);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setAsking(false);
    }
  };

  // Savetnik se pita sam za adresu koju geokoder nije umeo da postavi, i to tek
  // kad je neko otvori. Ranije je to bilo samo dugme, pa je predlog stajao
  // neviđen: vidi se prazna mapa i „nije nađena", i nema razloga pretpostaviti
  // da iza dugmeta nešto ima. Jednom po adresi — poziv se plaća.
  useEffect(() => {
    if (address.latitude !== null || askedFor.current === address.id) {
      return;
    }

    askedFor.current = address.id;
    void askAdviser();
    // Zavisi se samo od ŠIFRE otvorene adrese; askAdviser se menja pri svakom
    // iscrtavanju i uveo bi poziv u krug, a ceo objekat bi ga uvodio na svako
    // osvežavanje ekrana.
  }, [address.id, address.latitude]);

  const nepomerenNijeNaKuci = pin !== null
    && address.matchLevel !== 'HouseNumber'
    && address.latitude !== null && address.longitude !== null
    && Math.abs(pin.latitude - address.latitude) < 0.00001
    && Math.abs(pin.longitude - address.longitude) < 0.00001;

  // Pin stoji tačno gde ga je stavio predlog savetnika — niko ga nije pomerio.
  const pinJePredlog = pin !== null && predlogPin !== null
    && Math.abs(pin.latitude - predlogPin.latitude) < 0.00001
    && Math.abs(pin.longitude - predlogPin.longitude) < 0.00001;

  // Takav pin traži svesnu potvrdu kad predlog nije na kući ili ne liči na
  // upisanu ulicu — isto pravilo kao za netaknut pin mape (K10).
  const predlogNijeSiguran = pinJePredlog && predlogPin !== null
    && (predlogPin.matchLevel !== 'HouseNumber' || predlogPin.liciNaUpisano === false);

  const trebaSvesno = nepomerenNijeNaKuci || predlogNijeSiguran;

  const confirm = async () => {
    if (!pin) return;

    const name = signedIn.displayName;
    if (!name) {
      setError('Upiši ime pre potvrde — potvrda mora imati potpis.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await api.confirmAddress(address.id, {
        latitude: pin.latitude,
        longitude: pin.longitude,
        confirmedBy: name,
        resolvedCity: address.resolvedCity,
        postalCode: address.postalCode,
        floor: floor.trim() === '' ? null : Number(floor),
        hasLift,
        accessNotes: accessNotes.trim() || null,
        requiresTwoPeople: twoPeople,
        potvrdjujemNepomerenPin: svesno,
        predlogNivo: pinJePredlog && predlogPin ? predlogPin.matchLevel : null,
        predlogNeLici: pinJePredlog && predlogPin ? predlogPin.liciNaUpisano === false : null
      });
      onDone(address.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  /** Razlog odbijanja. Traži se u dijalogu, ne u prozorčiću pregledača. */
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const reject = async () => {
    const reason = rejectReason.trim();
    if (reason === '') return;

    setBusy(true);
    setError(null);
    try {
      await api.rejectAddress(address.id, reason, signedIn.displayName);
      setRejecting(false);
      setRejectReason('');
      onDone(address.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {error && <div className="health health--bad">{error}</div>}

      <p className={pin ? 'explanation' : 'explanation explanation--warn'}>{address.note}</p>

      {/* Kandidati mesta: ili mesto nije u šifarniku, ili ga nosi više sela —
          razlog iznad to kaže. Do 16.10.2026. je ovde uvek pisalo „nije u
          šifarniku", i za „Novo Selo" koje u šifarniku stoji dvaput (G8). */}
      {address.placeSuggestions.length > 0 && (
        <p className="note">
          Mesta za izbor:{' '}
          {address.placeSuggestions.map((s) => `${s.name} (${s.postalCode})`).join(', ')}.
          Pitaj kupca koje je i postavi pin tamo — ili ispravi mesto u ERP-u, pa pusti
          geokoder ponovo.
        </p>
      )}

      <AddressMap
        latitude={pin?.latitude ?? null}
        longitude={pin?.longitude ?? null}
        tight={address.matchLevel === 'HouseNumber'}
        onPick={(latitude, longitude) => setPin({ latitude, longitude })}
      />

      <p className="muted map__hint">
        {pin
          ? `Pin: ${pin.latitude.toFixed(5)}, ${pin.longitude.toFixed(5)} — klikni na mapu ili prevuci da ispraviš.`
          : 'Geokoder nije ponudio tačku. Klikni na mapu da postaviš pin.'}
      </p>

      {/* Odakle je pin, kad nije iz onoga što piše na otpremnici. Bez ove
          rečenice bi se tačka potvrdila ne znajući da je dobijena za drugačije
          napisanu ulicu. */}
      {pinFrom && (
        <p className="explanation">
          Pin je postavljen po predlogu <strong>{pinFrom}</strong>, ne po tekstu sa
          otpremnice. Proveri da je to ta kuća pre potvrde.
        </p>
      )}

      {/* Predlog ne menja adresu u ERP-u — samo pomera pin i kaže šta je
          verovatno pisalo. Ispravku teksta radi prodavnica, tamo gde je greška
          i nastala. */}
      <div className="suggest">
        <button type="button" onClick={() => void askAdviser()} disabled={asking}>
          {asking ? 'Pitam…' : 'Predloži ulicu'}
        </button>

        {suggestions?.length === 0 && unverified.length === 0 && (
          <span className="muted"> Savetnik nema predlog za ovu adresu.</span>
        )}

        {suggestions && suggestions.length > 0 && (
          <ul className="suggest__list">
            {suggestions.map((s) => (
              <li key={s.address}>
                <button
                  type="button"
                  onClick={() => {
                    setPin({ latitude: s.latitude, longitude: s.longitude });
                    setPinFrom(s.address);
                    setPredlogPin(s);
                    setSvesno(false);
                  }}
                >
                  <strong>{s.address}</strong>
                  {/* Predlog koji ne liči na upisano ume da bude druga ulica (G9). */}
                  <span className={s.liciNaUpisano === false ? 'flag flag--warn' : 'muted'}> {s.reason}</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Ovo mapa ne poznaje, pa nema tačku i ne može se kliknuti. Stoji jer
            onome ko pin postavlja rukom razlika između „Doze Djerdja" i „Dože
            Đerđa" kaže gde uopšte da gleda. */}
        {unverified.length > 0 && (
          <p className="muted suggest__unverified">
            Savetnik misli da je: {unverified.join(', ')} — ali te ulice nema na
            mapi, pa pin postavi ručno.
          </p>
        )}
      </div>

      <div className="access">
        <label className="field">
          Sprat
          <input
            type="number"
            value={floor}
            onChange={(e) => {
              setFloor(e.target.value);
              if (!twoPeopleTouched) {
                setTwoPeople(dvaCoveka(e.target.value.trim() === '' ? null : Number(e.target.value), hasLift));
              }
            }}
            placeholder="prizemlje"
          />
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={hasLift}
            onChange={(e) => {
              setHasLift(e.target.checked);
              if (!twoPeopleTouched) {
                setTwoPeople(dvaCoveka(floor.trim() === '' ? null : Number(floor), e.target.checked));
              }
            }}
          />
          Ima lift
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={twoPeople}
            onChange={(e) => { setTwoPeopleTouched(true); setTwoPeople(e.target.checked); }}
          />
          Traži dva čoveka
        </label>
      </div>

      <label className="field">
        Pristup
        <input
          type="text"
          value={accessNotes}
          onChange={(e) => setAccessNotes(e.target.value)}
          placeholder="uzak prolaz, kapija iza zgrade…"
        />
      </label>

      {/* PIN MAPE KOJI NIJE NA KUĆI. Do 15.10.2026. je jedan klik pretvarao
          netaknut pin centra mesta u „kućni broj, potvrdio operater" (G7). */}
      {nepomerenNijeNaKuci && (
        <label className="check">
          <input type="checkbox" checked={svesno} onChange={(e) => setSvesno(e.target.checked)} />
          Pin nije na kući ({address.matchLevel === 'Place' ? 'centar mesta' : 'ulica'}) i nisam ga
          pomerao — ipak je ovo tačno mesto
        </label>
      )}

      {/* PIN IZ PREDLOGA SAVETNIKA. Do 16.10.2026. je jedan klik pravio od
          predloga na nivou ulice „kućni broj, potvrdio operater" (K10). */}
      {!nepomerenNijeNaKuci && predlogNijeSiguran && predlogPin && (
        <label className="check">
          <input type="checkbox" checked={svesno} onChange={(e) => setSvesno(e.target.checked)} />
          Pin je postavio savetnik
          ({predlogPin.matchLevel === 'HouseNumber' ? 'kućni broj'
            : predlogPin.matchLevel === 'Place' ? 'centar mesta' : 'ulica'}
          {predlogPin.liciNaUpisano === false ? ', ne liči na upisanu ulicu' : ''}) i nisam ga
          pomerao — ipak je ovo tačno mesto
        </label>
      )}

      <div className="queue__confirm">
        <button
          type="button"
          onClick={() => void confirm()}
          disabled={busy || !pin || (trebaSvesno && !svesno)}
        >
          Potvrdi adresu
        </button>
        <button
          type="button"
          className="link link--danger"
          onClick={() => setRejecting(true)}
          disabled={busy}
        >
          Odbij
        </button>

        {rejecting && (
          <Dialog
            title="Odbijanje adrese"
            description={
              <>Adresa se sklanja sa spiska i isporuka se ne može planirati dok se
              ne sredi. Razlog ostaje zapisan uz nju.</>
            }
            confirmLabel="Odbij adresu"
            danger
            busy={busy}
            disabled={rejectReason.trim() === ''}
            onCancel={() => { setRejecting(false); setRejectReason(''); }}
            onConfirm={() => void reject()}
          >
            <label>
              Zašto se adresa odbija?
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="npr. kupac je dao adresu koja ne postoji, čeka se ispravka"
              />
            </label>
          </Dialog>
        )}
      </div>
    </>
  );
}
